import { useEffect } from 'react';
import { ArrowRightIcon, InfoIcon } from 'lucide-react';
import { images, routes } from '../data/site';
import { SiteLink } from '../components/SiteLink';
const TITLE = 'Hot Tub Relaxation & Everyday Wellness | Pegasus Spas';
const DESCRIPTION = 'Explore how a hot tub can create more room for relaxation, quiet time, conversation and outdoor enjoyment at home. Visit Pegasus Spas in Brooklyn, Michigan.';
const CANONICAL = 'https://www.pegasus-spas.com/health-benefits';
const feelDifferent = [{
  term: 'Warm water',
  detail: 'A comfortable change of pace after a long or cold day.'
}, {
  term: 'Buoyancy',
  detail: 'The water supports you while you settle into the seat.'
}, {
  term: 'Jets',
  detail: 'Different seats and jet arrangements create different kinds of pressure and movement.'
}];
const moments = [{
  title: 'Coffee on a Cool Morning',
  copy: 'Twenty minutes outside with a cup of coffee, before the day starts asking for anything.',
  image: images.pathHotTubs,
  alt: 'Hot tub with clear water and jets running on a residential wood deck surrounded by trees',
  imageClass: 'object-center'
}, {
  title: 'Steam on a Cold Michigan Night',
  copy: 'Warm water and a little steam are a big part of why people here stay outside in winter.',
  image: images.sundanceCouple,
  alt: 'A couple relaxing together in a hot tub',
  imageClass: 'object-center'
}, {
  title: 'After Work or a Day in the Yard',
  copy: 'Mowing, hauling, a long shift on your feet — warm water is a good place to sit down after.',
  image: images.sundance,
  alt: 'Hot tub in a landscaped residential backyard at the end of the day',
  imageClass: 'object-center'
}];
const relatedLinks = [{
  label: 'Explore Hot Tubs',
  href: routes.hotTubs
}, {
  label: 'Spa Care & FAQs',
  href: routes.spaCare
}, {
  label: 'Contact Us',
  href: routes.contact
}];
function upsertMeta(name: string, content: string) {
  let el = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}
export function HealthBenefitsPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = TITLE;
    upsertMeta('description', DESCRIPTION);
    let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', CANONICAL);
  }, []);
  return <main data-id="element-1380" data-mp="16xvijkz5qsj4">
      {/* Opening: photograph first, words underneath */}
      <section aria-labelledby="health-h1" className="w-full bg-white" data-id="element-1381" data-mp="afpjjtqrc2op">
        <div className="w-full" data-id="element-1382" data-mp="admwnikwipo7">
          <div className="h-[240px] w-full overflow-hidden sm:h-[340px] lg:h-[420px]" data-id="element-1383" data-mp="eh2rqahbohys">
            <img src={images.hero} alt="Open hot tub beside a well-maintained Michigan lake home and backyard" className="h-full w-full object-cover object-[60%_58%] sm:object-[55%_56%] lg:object-[50%_54%]" decoding="async" data-id="element-1384" data-mp="6xtf88dis35i" />
          </div>
        </div>

        <div className="mx-auto max-w-container px-5 py-10 sm:px-8 lg:py-14" data-id="element-1385" data-mp="3p3y5iqe7ui1d">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-14" data-id="element-1386" data-mp="3k4brieqkcxri">
            <div className="lg:col-span-7" data-id="element-1387" data-mp="3k6eltayr1d21">
              <p className="text-[14px] font-bold uppercase tracking-[0.02em] text-pegasus-blue" data-id="element-1388" data-mp="3fdd4yxezf3ft">
                Everyday Well-Being at Home
              </p>
              <h1 id="health-h1" className="mt-3 font-display text-h1-mobile font-semibold text-ink lg:text-[48px] lg:leading-[56px]" data-id="element-1389" data-mp="3ac5x6ycua495">
                Warm Water, Quiet Time and Room to Unwind
              </h1>
            </div>

            <div className="lg:col-span-5 lg:pt-3" data-id="element-1390" data-mp="2wfj08vkvze38">
              <p className="text-body leading-8 text-ink/80 sm:text-body-lg" data-id="element-1391" data-mp="10b2y0w5wypsl">
                A hot tub can be one of the easiest places at home to slow down. It may be twenty
                quiet minutes after work, coffee outside on a cool morning, an unhurried
                conversation or simply warm water after a long day.
              </p>

              <div className="mt-6 flex flex-wrap items-center gap-3" data-id="element-1392" data-mp="vit9nkkg5xsf">
                <SiteLink to={routes.hotTubs} className="inline-flex min-h-[48px] items-center rounded-btn bg-pegasus-magenta px-6 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-[#bb2f83]" data-id="element-1393" data-mp="hkbqm05hl9gu">
                  Explore Hot Tubs
                </SiteLink>
                <SiteLink to={routes.location} className="inline-flex min-h-[48px] items-center rounded-btn border border-ink/25 bg-white px-5 text-[16px] font-medium text-ink transition-colors duration-150 ease-out hover:border-pegasus-blue" data-id="element-1394" data-mp="cnv1b2mjwp26">
                  Visit the Showroom
                </SiteLink>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Moments: one people-centered lead, then a compact three-up row */}
      <section aria-labelledby="moments-heading" className="w-full bg-water" data-id="element-1395" data-mp="c2gahyx45ay">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-14" data-id="element-1396" data-mp="9y2977b1pwy">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:items-end lg:gap-12" data-id="element-1397" data-mp="e1cmfhcy7x5l">
            <div className="lg:col-span-7" data-id="element-1398" data-mp="93ktck0ki4zt">
              <div className="aspect-[4/3] w-full overflow-hidden rounded-card sm:aspect-[16/9]" data-id="element-1399" data-mp="o13hbk5f8e67">
                <img src={images.backyardLiving} alt="Friends relaxing around a fire table beside a Big Green Egg and a hot tub integrated into the wood deck." className="h-full w-full object-cover object-center" loading="lazy" decoding="async" data-id="element-1400" data-mp="j2jt1d3cuqao" />
              </div>
            </div>

            <div className="lg:col-span-5 lg:pb-1" data-id="element-1401" data-mp="3eje67e1mtfwx">
              <h2 id="moments-heading" className="font-display text-h2-mobile font-semibold text-ink lg:text-h2-desktop" data-id="element-1402" data-mp="3ek5qhsps3r7w">
                The Moments People Make Room For
              </h2>
              <h3 className="mt-6 font-display text-[24px] font-semibold leading-8 text-ink sm:text-[27px]" data-id="element-1403" data-mp="3of7g0pnyuvb0">
                When a Couple or a Family Actually Talks
              </h3>
              <p className="mt-3 text-body leading-8 text-ink/80 sm:text-body-lg" data-id="element-1404" data-mp="1arjs803iq27">
                Without a television or a table full of distractions, conversation has room to
                stretch out. For a lot of households, that dependable half hour outside with the
                people who matter is the whole point.
              </p>
            </div>
          </div>

          <ul className="mt-10 grid grid-cols-1 gap-9 sm:grid-cols-3 sm:gap-7 lg:mt-12 lg:gap-9" data-id="element-1405" data-mp="l1d23ihjwcpu">
            {moments.map(moment => <li key={moment.title} data-id="element-1406" data-mp="20jst747jb74w">
                <div className="aspect-[4/3] w-full overflow-hidden rounded-card" data-id="element-1407" data-mp="20irhcxip2gm4">
                  <img src={moment.image} alt={moment.alt} className={`h-full w-full object-cover ${moment.imageClass}`} loading="lazy" decoding="async" data-id="element-1408" data-mp="1vk6h4y3fzwxo" />
                </div>
                <h3 className="mt-4 font-display text-[21px] font-semibold leading-8 text-ink sm:text-[22px]" data-id="element-1409" data-mp="2j9r1nlf8v292">
                  {moment.title}
                </h3>
                <p className="mt-1.5 text-[16.5px] leading-7 text-ink/80" data-id="element-1410" data-mp="2ehhico8mx0fr">{moment.copy}</p>
              </li>)}
          </ul>
        </div>
      </section>

      {/* Why it feels different */}
      <section aria-labelledby="different-heading" className="w-full bg-white" data-id="element-1411" data-mp="371prdaffx05q">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-16" data-id="element-1412" data-mp="327txok67lzgd">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14" data-id="element-1413" data-mp="2aks81tr0i6nz">
            <div className="lg:col-span-5" data-id="element-1414" data-mp="2fcjxu22cg8d2">
              <h2 id="different-heading" className="font-display text-h2-mobile font-semibold text-ink lg:text-h2-desktop" data-id="element-1415" data-mp="2feelvp8wwgld">
                Why a Hot Tub Feels Different
              </h2>
              <p className="mt-5 text-body leading-8 text-ink/80 sm:text-body-lg" data-id="element-1416" data-mp="2u9ua3dmt9i9u">
                The appeal is simple: warmth, buoyancy and moving water in a place where you can
                stop doing everything else for a while.
              </p>
            </div>

            <div className="lg:col-span-7" data-id="element-1417" data-mp="2kdr7k6o1rhmd">
              <dl className="divide-y divide-ink/15 border-y border-ink/15" data-id="element-1418" data-mp="2p9onibveelym">
                {feelDifferent.map(item => <div key={item.term} className="py-5 sm:grid sm:grid-cols-12 sm:gap-8" data-id="element-1419" data-mp="18un4c95dv3ik">
                    <dt className="font-display text-[21px] font-semibold leading-8 text-ink sm:col-span-4" data-id="element-1420" data-mp="1dw41puons7ue">
                      {item.term}
                    </dt>
                    <dd className="mt-1.5 text-[16.5px] leading-7 text-ink/75 sm:col-span-8 sm:mt-0" data-id="element-1421" data-mp="11d8hfynsr4zp">
                      {item.detail}
                    </dd>
                  </div>)}
              </dl>

              <p className="mt-6 text-body leading-8 text-ink/85" data-id="element-1422" data-mp="1aegao2bghfmx">
                The best way to understand those differences is to sit in the spa and compare the
                seats in person.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Responsible use */}
      <section aria-labelledby="responsible-heading" className="w-full bg-white" data-id="element-1423" data-mp="wedymwptxgfl">
        <div className="mx-auto max-w-container px-5 pt-12 sm:px-8 lg:pt-14" data-id="element-1424" data-mp="rlum832vorcd">
          <div className="max-w-3xl rounded-card bg-water px-6 py-6 sm:px-8" data-id="element-1425" data-mp="3wt7xz7d6lz6">
            <div className="flex items-start gap-3.5" data-id="element-1426" data-mp="3v3m8d0uc2h2n">
              <InfoIcon className="mt-1 h-5 w-5 shrink-0 text-pegasus-blue" aria-hidden="true" data-id="element-1427" data-mp="3v2ukkc6z9nmx" />
              <div data-id="element-1428" data-mp="364uq0qd8ptje">
                <h2 id="responsible-heading" className="text-[19px] font-semibold leading-7 text-ink" data-id="element-1429" data-mp="365e3nuvuxvrm">
                  Enjoy Your Spa Responsibly
                </h2>
                <p className="mt-2 text-[16.5px] leading-7 text-ink/80" data-id="element-1430" data-mp="2s6eiix7jzz07">
                  Follow the spa owner&rsquo;s manual and product directions, keep children
                  supervised, and leave the water if you feel unwell. If you have a personal health
                  concern about heat or immersion, ask your healthcare provider what is appropriate
                  for you.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Close */}
      <section aria-labelledby="close-heading" className="w-full bg-white" data-id="element-1431" data-mp="24gk4wdfeu6ew">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-16" data-id="element-1432" data-mp="24imuwxgkbzjm">
          <div className="max-w-3xl" data-id="element-1433" data-mp="1ziindwxmuf5j">
            <p className="text-[14px] font-bold uppercase tracking-[0.02em] text-pine" data-id="element-1434" data-mp="ezydgej9qdx3">
              Find Your Fit
            </p>
            <h2 id="close-heading" className="mt-2 font-display text-h2-mobile font-semibold text-ink lg:text-[34px] lg:leading-[42px]" data-id="element-1435" data-mp="jxq65arqm42c">
              Find a Hot Tub That Fits the Way You Relax
            </h2>
            <p className="mt-4 text-body text-ink/80 sm:text-body-lg" data-id="element-1436" data-mp="sx3d9wse2gjw">
              Visit us in Brooklyn to compare seating, size, controls and the feel of different
              layouts in person. We can help you narrow the choices around your home and the
              way you expect to use the spa.
            </p>
          </div>

          <div className="mt-7 flex flex-wrap items-center gap-3" data-id="element-1437" data-mp="a0v5al7zrrkw">
            <SiteLink to={routes.hotTubs} className="inline-flex min-h-[48px] items-center rounded-btn bg-pegasus-magenta px-6 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-[#bb2f83]" data-id="element-1438" data-mp="38qlkmcl1sisj">
              Explore Hot Tubs
            </SiteLink>
            <SiteLink to={routes.location} className="inline-flex min-h-[48px] items-center rounded-btn border border-ink/25 bg-white px-5 text-[16px] font-medium text-ink transition-colors duration-150 ease-out hover:border-pegasus-blue" data-id="element-1439" data-mp="fwn1n050o0i0">
              Plan a Showroom Visit
            </SiteLink>
          </div>
        </div>
      </section>

      {/* Related links */}
      <section aria-label="Related pages" className="w-full border-t border-ink/10 bg-white" data-id="element-1440" data-mp="3t3f2qqrdcw84">
        <div className="mx-auto max-w-container px-5 py-6 sm:px-8" data-id="element-1441" data-mp="axah4m1mb5il">
          <ul className="flex flex-col gap-x-8 gap-y-1 sm:flex-row sm:flex-wrap sm:items-center" data-id="element-1442" data-mp="5xxiz55aqc0p">
            {relatedLinks.map(link => <li key={link.label} data-id="element-1443" data-mp="1mb5wnvwfpgac">
                <SiteLink to={link.href} className="inline-flex min-h-[44px] items-center gap-1.5 text-[16px] font-medium text-pegasus-blue underline decoration-pegasus-blue/40 underline-offset-4 transition-colors duration-150 ease-out hover:text-pegasus-magenta hover:decoration-pegasus-magenta" data-id="element-1444" data-mp="1mea7vd8bhcai">
                  {link.label}
                  <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1445" data-mp="1fxi5gvz0sj5b" />
                </SiteLink>
              </li>)}
          </ul>
        </div>
      </section>
    </main>;
}